import React, { useState, useEffect } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Typography, Divider } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import ReorderIcon from '@mui/icons-material/Reorder'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  accordionSummaryStyles,
  AlertDialog,
  cardStyles,
  IconButtonWithTooltip,
  useGlobalMessageHandler,
  useUsUrlDescTypeLuts,
  DragAndDropStack
} from 'components'
import { createUsPersonUrl, deleteUsPersonUrl, type PersonUrl, updateUsPersonUrl } from 'services'
import { updateUsPersonUrlDisplayOrder } from 'services/person/us/edit/personUrlService'
import { type LegislativeRoles } from 'services/person/us/edit/legislativeRolesService'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import { type ExecCandidate } from 'services/candidate/candidateService'
import { UrlCardItem } from './url-card-item'
import { getEmptyPersonUrl } from 'helpers'
import { extractRoleIds } from 'helpers/person/personHelper'
import { type HttpResponse } from 'interfaces'

interface UrlCardProps {
  personId: number
  urls: PersonUrl[]
  loading: boolean
  setUrls: React.Dispatch<React.SetStateAction<PersonUrl[]>>
  roles?: LegislativeRoles
  execCandidates?: ExecCandidate[]
  executiveRoles?: ExecutivePosition[]
}

interface RoleUrlGroup {
  roleId: number
  roleName: string
  roleOrder: number
  urls: PersonUrl[]
}

export const UrlCard: React.FC<UrlCardProps> = ({
  urls,
  loading,
  setUrls,
  personId,
  roles,
  execCandidates,
  executiveRoles
}) => {
  const [expanded, setExpanded] = useState(true)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()
  const [urlToAdd, setUrlToAdd] = useState<PersonUrl | null>(null)
  const [urlToDelete, setUrlToDelete] = useState<PersonUrl | null>(null)
  const [deleteCallback, setDeleteCallback] = useState<(() => void) | null>(null)
  const [roleUrlGroups, setRoleUrlGroups] = useState<RoleUrlGroup[]>([])
  const [reorderingEnabled, setReorderingEnabled] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const { lutValues: { descTypes } } = useUsUrlDescTypeLuts()
  const [personRoleIds, setPersonRoleIds] = useState<number[]>([])
  const [fetchedPersonRoles, setFetchedPersonRoles] = useState<number[]>([])

  // Extract role IDs from passed roles data
  useEffect(() => {
    if (roles !== undefined && roles !== null && personId !== 0 && personId !== null && personId !== undefined) {
      const execCandidatesData = execCandidates ?? []
      const executiveRolesData = executiveRoles ?? []
      const roleIds = extractRoleIds(roles, execCandidatesData, executiveRolesData)
      setFetchedPersonRoles(roleIds)
    } else {
      setFetchedPersonRoles([])
    }
  }, [personId, roles, execCandidates, executiveRoles])

  // Combine person roles with existing URL roles (when URLs or descTypes change)
  useEffect(() => {
    const existingUrlRoleIds = new Set<number>()
    urls.forEach(url => {
      if (url.descId != null) {
        const descType = Array.from(descTypes.values()).find(desc => desc.id === url.descId)
        if (descType?.role?.id != null) {
          existingUrlRoleIds.add(descType.role.id)
        }
      }
    })

    const existingUrlRoleIdsArray = Array.from(existingUrlRoleIds)
    const allRoleIds = [...fetchedPersonRoles, ...existingUrlRoleIdsArray]
    const combinedRoleIds = Array.from(new Set(allRoleIds))

    // Only filter roles if we have any roles (person roles OR existing URL roles)
    // If no roles found at all, set empty array to show all roles (no filtering)
    if (combinedRoleIds.length !== 0) {
      setPersonRoleIds(combinedRoleIds)
    } else {
      setPersonRoleIds([])
    }
  }, [fetchedPersonRoles, urls, descTypes])

  useEffect(() => {
    if (savingOrder || reorderingEnabled) {
      return
    }

    // Role-based grouping for display
    const roleGroupMap = new Map<number, RoleUrlGroup>()

    // Sort URLs first by description order, then by display order to ensure consistent processing
    const sortedUrls = [...urls].sort((a, b) => {
      const aDescType = Array.from(descTypes.values()).find(desc => desc.id === a.descId)
      const bDescType = Array.from(descTypes.values()).find(desc => desc.id === b.descId)

      const aDescOrder = aDescType?.lutOrder ?? 999
      const bDescOrder = bDescType?.lutOrder ?? 999

      if (aDescOrder !== bDescOrder) {
        return aDescOrder - bDescOrder
      }
      return a.displayOrder - b.displayOrder
    })

    sortedUrls.forEach(url => {
      if (url.descId !== undefined && url.descId !== null) {
        const urlDescType = Array.from(descTypes.values()).find(desc => desc.id === url.descId)
        if (urlDescType?.role !== null && urlDescType?.role !== undefined) {
          const roleId = urlDescType.role.id
          const roleName = urlDescType.role.desc !== '' ? urlDescType.role.desc : 'Unknown Role'
          const roleOrder = urlDescType.lutOrder !== 0 ? urlDescType.lutOrder : 999

          if (!roleGroupMap.has(roleId)) {
            roleGroupMap.set(roleId, {
              roleId,
              roleName,
              roleOrder,
              urls: []
            })
          }
          const roleGroup = roleGroupMap.get(roleId)
          if (roleGroup !== undefined) {
            roleGroup.urls.push(url)
          }
        }
      }
    })

    const roleGroups = Array.from(roleGroupMap.values())
      .sort((a, b) => a.roleOrder - b.roleOrder)

    roleGroups.forEach(group => {
      group.urls.sort((a, b) => {
        const aDescType = Array.from(descTypes.values()).find(desc => desc.id === a.descId)
        const bDescType = Array.from(descTypes.values()).find(desc => desc.id === b.descId)

        const aDescOrder = aDescType?.lutOrder ?? 999
        const bDescOrder = bDescType?.lutOrder ?? 999

        if (aDescOrder !== bDescOrder) {
          return aDescOrder - bDescOrder
        }
        return a.displayOrder - b.displayOrder
      })
    })

    setRoleUrlGroups(roleGroups)
  }, [urls, descTypes, reorderingEnabled, savingOrder])

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const updateUrl = (updatedUrl: PersonUrl, onComplete: () => void): void => {
    const isNewUrl = urlToAdd?.id === updatedUrl.id
    const promise = isNewUrl
      ? createUsPersonUrl(personId, updatedUrl)
      : updateUsPersonUrl(updatedUrl.id, updatedUrl)

    void promise.then(response => {
      if (!response.succeeded || response.result == null) {
        showErrorMessage('Failed to save URL')
        onComplete()
        return
      }

      showSuccessMessage(`URL successfully ${isNewUrl ? 'created' : 'updated'}`)
      setUrls((prevUrls) =>
        isNewUrl && response.result != null
          ? [...prevUrls, response.result]
          : prevUrls.map(url => url.id === response.result?.id ? response.result : url)
      )

      if (isNewUrl) {
        setUrlToAdd(null)
      }
      onComplete()
    }).catch(() => {
      showErrorMessage('Failed to save URL')
      onComplete()
    })
  }

  const deleteUrl = (): void => {
    if (urlToDelete == null) return
    deleteUsPersonUrl(urlToDelete.id)
      .then(() => {
        setUrls(prevUrls => prevUrls.filter(url => urlToDelete.id !== url.id))
        showSuccessMessage('URL deleted successfully')
        deleteCallback?.()
      })
      .catch(() => {
        showErrorMessage('Failed to delete URL')
        deleteCallback?.()
      })
      .finally(() => {
        setDeleteCallback(null)
      })
  }

  const addUrl = (): void => {
    setUrlToAdd(getEmptyPersonUrl(personId, urls.length + 1))
  }

  const openDeleteConfirm = (urlToDelete: PersonUrl, onComplete: () => void): void => {
    if (urlToAdd != null && urlToDelete.id === urlToAdd.id) {
      setUrlToAdd(null)
      onComplete()
      return
    }
    setUrlToDelete(urlToDelete)
    setDeleteConfirmationOpen(true)
    // Store the callback to call it when the delete operation completes
    setDeleteCallback(() => onComplete)
  }

  const handleUpdateDisplayOrder = async (items: PersonUrl[], itemSelected: PersonUrl): Promise<HttpResponse<any[]>> => {
    const filteredItems = items.filter(url =>
      url.descId === itemSelected.descId
    )
    setRoleUrlGroups(prevRoleGroups => {
      return prevRoleGroups.map(roleGroup => {
        const hasSelectedItem = roleGroup.urls.some(url => url.id === itemSelected.id)

        if (hasSelectedItem) {
          const otherUrls = roleGroup.urls.filter(url =>
            url.descId !== itemSelected.descId
          )
          return {
            ...roleGroup,
            urls: [...otherUrls, ...filteredItems]
          }
        }

        return roleGroup
      })
    })
    return {
      succeeded: true,
      result: [],
      status: 200,
      message: 'Success',
      errors: {} as any
    }
  }

  const saveReorderedUrls = async (): Promise<void> => {
    setSavingOrder(true)

    try {
      const displayOrderById: Record<number, number> = {}
      roleUrlGroups.forEach(roleGroup => {
        const urlsByDescId = roleGroup.urls.reduce<Record<number, PersonUrl[]>>((acc, url) => {
          const descId = url.descId !== undefined && url.descId !== null ? url.descId : 0
          if (acc[descId] === undefined) {
            acc[descId] = []
          }
          acc[descId].push(url)
          return acc
        }, {})

        Object.values(urlsByDescId).forEach(descUrls => {
          descUrls.forEach((url, index) => {
            displayOrderById[url.id] = index + 1
          })
        })
      })

      let firstUrl: PersonUrl | undefined
      for (const roleGroup of roleUrlGroups) {
        if (roleGroup.urls.length > 0) {
          firstUrl = roleGroup.urls[0]
          break
        }
      }

      if (firstUrl === undefined || firstUrl === null) {
        showErrorMessage('No URLs to reorder')
        return
      }

      const response = await updateUsPersonUrlDisplayOrder(
        personId,
        displayOrderById,
        firstUrl.id,
        firstUrl.typeId !== undefined && firstUrl.typeId !== null ? firstUrl.typeId : 0
      )

      if (response.succeeded) {
        setUrls(prevUrls => {
          return prevUrls.map(url => {
            if (Object.prototype.hasOwnProperty.call(displayOrderById, url.id)) {
              return { ...url, displayOrder: displayOrderById[url.id] }
            }
            return url
          })
        })

        showSuccessMessage('URL order updated successfully')
        setReorderingEnabled(false)
      } else {
        showErrorMessage('Failed to update URL order')
      }
    } catch (error) {
      showErrorMessage('Failed to update URL order')
      console.error('Error updating URL display order:', error)
    } finally {
      setSavingOrder(false)
    }
  }

  const toggleReordering = (): void => {
    if (reorderingEnabled) {
      void saveReorderedUrls()
    } else {
      setReorderingEnabled(true)
    }
  }

  return (
    <Card sx={cardStyles}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
        >
          <Typography variant="h6">Emails and Websites</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            {reorderingEnabled && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <IconButtonWithTooltip
                  label={savingOrder ? 'Saving...' : 'Save URL order'}
                  color="primary"
                  onClick={() => { void saveReorderedUrls() }}
                  disabled={loading || savingOrder}
                  icon={<SaveIcon />}
                />
                <IconButtonWithTooltip
                  label="Cancel reordering"
                  color="secondary"
                  onClick={() => {
                    setReorderingEnabled(false)
                  }}
                  disabled={loading || savingOrder}
                  icon={<CancelIcon />}
                />
              </Box>
            )}

            {reorderingEnabled
              ? (
                  roleUrlGroups.map((roleGroup, roleIndex) => (
                    <React.Fragment key={`role-group-${roleGroup.roleId}`}>
                      {roleIndex > 0 && roleUrlGroups.length > 1 && <Divider sx={{ my: 3 }} />}
                      {roleUrlGroups.length > 1 && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                            pb: 0.5
                          }}
                        >
                          {roleGroup.roleName}
                        </Typography>
                      )}

                      {/* Group URLs by description within this role */}
                      {Object.entries(
                        roleGroup.urls.reduce<Record<number, PersonUrl[]>>((acc, url) => {
                          const descId = url.descId !== undefined && url.descId !== null ? url.descId : 0
                          if (acc[descId] === undefined) {
                            acc[descId] = []
                          }
                          acc[descId].push(url)
                          return acc
                        }, {})
                      )
                        .sort(([descIdA], [descIdB]) => {
                          const descA = Array.from(descTypes.values()).find(desc => desc.id === parseInt(descIdA))
                          const descB = Array.from(descTypes.values()).find(desc => desc.id === parseInt(descIdB))
                          const orderA = descA?.lutOrder ?? 999
                          const orderB = descB?.lutOrder ?? 999
                          return orderA - orderB
                        })
                        .map(([descId, descUrls], descIndex) => {
                          const descIdNum = parseInt(descId)
                          const urlDesc = Array.from(descTypes.values()).find(desc => desc.id === descIdNum)
                          const descName = urlDesc?.desc ?? 'Other'
                          const typeName = urlDesc?.type?.desc ?? 'Unknown Type'

                          return (
                            <React.Fragment key={`desc-${descId}`}>
                              {descIndex > 0 && <Divider sx={{ my: 2 }} />}
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, ml: 2 }}>
                                {typeName}: {descName}
                              </Typography>

                              <Box sx={{ ml: 2 }}>
                                <DragAndDropStack
                                  originalItems={descUrls}
                                  disableDrag={loading || descUrls.length <= 1}
                                  makeGray={false}
                                  id={`url-role-${roleGroup.roleId}-desc-${descId}`}
                                  itemRenderer={(url: PersonUrl) => (
                                    <Box
                                      sx={{
                                        p: 1,
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        backgroundColor: 'transparent',
                                        cursor: 'grab'
                                      }}
                                    >
                                      <Typography>{url.value}</Typography>
                                    </Box>
                                  )}
                                  updateDisplayOrder={handleUpdateDisplayOrder}
                                  suppressSuccessMessages={true}
                                />
                              </Box>
                            </React.Fragment>
                          )
                        })}
                    </React.Fragment>
                  ))
                )
              : (
                  roleUrlGroups.map((roleGroup, groupIndex) => (
                    <React.Fragment key={`role-group-${roleGroup.roleId}`}>
                      {groupIndex > 0 && roleUrlGroups.length > 1 && <Divider sx={{ my: 3 }} />}
                      {roleUrlGroups.length > 1 && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                            pb: 0.5
                          }}
                        >
                          {roleGroup.roleName}
                        </Typography>
                      )}

                      {roleGroup.urls.map((url) => (
                        <UrlCardItem
                          key={url.id}
                          originalUrl={url}
                          onSave={updateUrl}
                          onDelete={openDeleteConfirm}
                          roleIds={personRoleIds}
                        />
                      ))}
                    </React.Fragment>
                  ))
                )
            }

            {!reorderingEnabled && urlToAdd != null && (
              <UrlCardItem
                key={urlToAdd.id}
                originalUrl={urlToAdd}
                onSave={updateUrl}
                onDelete={openDeleteConfirm}
                startDisabled={false}
                roleIds={personRoleIds}
              />
            )}
          </CardContent>
        </AccordionDetails>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mr: 2, mb: 2 }}>
          <IconButtonWithTooltip
            label="Add new URL"
            color="primary"
            onClick={addUrl}
            disabled={loading || reorderingEnabled}
            icon={<AddIcon />}
          />
          {!reorderingEnabled && (roleUrlGroups.length > 1 || urls.length > 1) && (
            <IconButtonWithTooltip
              label="Reorder URLs"
              color="primary"
              onClick={toggleReordering}
              disabled={loading}
              icon={<ReorderIcon />}
            />
          )}
        </Box>
      </Accordion>
      <AlertDialog
        title="Delete Confirmation"
        contentText="Are you sure you want to delete this URL?"
        nameButtonCancel="Cancel"
        nameButtonConfirm="Delete"
        onButtonClick={deleteUrl}
        onClose={() => { setDeleteConfirmationOpen(false) }}
        open={deleteConfirmationOpen}
      />
    </Card>
  )
}

export default UrlCard
